import { Account } from 'api/Account/types';
import { useAccount } from 'components/AdminDashboard/components/AccountsPage/components/AccountModal/hooks';
import { FormTextField } from 'common/components';
import usePaginationSearchParams from 'common/hooks/usePaginationSearchParams';

import React, { SetStateAction, Dispatch } from 'react';
import { Button, Grid, Modal, Paper, Typography } from '@mui/material';
import { FormikProvider } from 'formik';
import LoadingButton from '@mui/lab/LoadingButton';

interface Props {
  account: Account | null;
  onCancel: () => void;
  setAccounts: Dispatch<SetStateAction<Account[]>>;
}

const AccountModal: React.FC<Props> = ({ onCancel, account, setAccounts }) => {
  const { rowsParam } = usePaginationSearchParams();

  const onSubmitted = (updatedAccount: Account) => {
    if (account === null) {
      setAccounts((prevState) => {
        if (prevState.length >= Number(rowsParam)) {
          prevState.pop();
        }
        prevState.unshift(updatedAccount);

        return prevState;
      });
    } else {
      setAccounts((prevState) => {
        const accountIndex = prevState.findIndex(account => account.id === updatedAccount.id);

        return [
          ...prevState.slice(0, accountIndex),
          updatedAccount,
          ...prevState.slice(accountIndex + 1, prevState.length),
        ];
      });
    }

    onCancel();
  };

  const { formik, isSubmitting, submitError } = useAccount(onSubmitted, account);

  return (
    <Modal open onClose={onCancel}>
      <Paper
        sx={{
          borderRadius: '20px',
          elevation: 9,
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          outline: 'none',
          padding: 3,
          width: { xs: '100%', md: '600px' },
        }}
        data-testid="account-modal"
      >
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <Grid display="flex" flexDirection="column" item container spacing={3}>

              <Grid item>
                <Typography>
                  {account === null ? 'Add an' : 'Edit'} account
                </Typography>
              </Grid>

              <Grid item xs={12} display="flex" flexDirection="column" sx={{ maxHeight: 600, overflowY: 'auto' }}>

                <Grid item marginBottom={3} marginRight={3}>
                  <FormTextField
                    id="accountId"
                    name="accountId"
                    label="Account Id"
                    value={formik.values.accountId}
                    onChange={formik.handleChange}
                    error={formik.errors.accountId}
                    touched={formik.touched.accountId}
                    disabled={isSubmitting}
                  />
                </Grid>

                <Grid item marginRight={3}>
                  <FormTextField
                    id="name"
                    name="name"
                    label="Name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    error={formik.errors.name}
                    touched={formik.touched.name}
                    disabled={isSubmitting}
                  />
                </Grid>

              </Grid>

              {submitError && (
                <Grid item xs={12}>
                  <Typography color="error" variant="subtitle2">
                    {submitError}
                  </Typography>
                </Grid>
              )}

              <Grid container spacing={2} item xs={12}>
                <Grid item>
                  <LoadingButton
                    disabled={isSubmitting}
                    variant="contained"
                    data-testid="button-add-account"
                    type="submit"
                  >
                    Submit
                  </LoadingButton>
                </Grid>
                <Grid item>
                  <Button variant="outlined" onClick={onCancel} disabled={isSubmitting}>
                    Cancel
                  </Button>
                </Grid>
              </Grid>

            </Grid>
          </form>
        </FormikProvider>
      </Paper>
    </Modal>
  );
};

export default AccountModal;
